import { PublicClientApplication } from '@azure/msal-browser';
import { create } from 'zustand';

import handleException from '@/common/utils/errors';

export interface TeamsEvent {
  id: string;
  title: string;
  time: string;
  joinUrl?: string;
  startTime: Date;
  endTime: Date;
}

interface TeamsCalendarItem {
  id: string;
  subject: string;
  start: { dateTime: string };
  end: { dateTime: string };
  onlineMeeting?: { joinUrl: string };
}

interface TeamsScheduleState {
  events: TeamsEvent[];
  loading: boolean;
  teamsAuthenticated: boolean;
  msalInstance: PublicClientApplication | null;
  checkAuthState: () => Promise<void>;
  fetchEvents: () => Promise<void>;
  authenticate: () => Promise<void>;
  disconnect: () => Promise<void>;
}

// Microsoft Azure AD configuration
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_PUBLIC_AZURE_CLIENT_ID!,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false, // Disables WAM Broker
    loggerOptions: {
      loggerCallback: () => {},
      piiLoggingEnabled: false,
      logLevel: 3, // Error
    }
  }
};

let msalInstance: PublicClientApplication | null = null;

const initializeMsal = async () => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();

    // Handle the redirect flow
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        // Handle successful authentication
        const account = response.account;
        if (account) {
          msalInstance.setActiveAccount(account);
        }
      }
    } catch (err) {
      handleException(err);
    }
  }
  return msalInstance;
};

export const useTeamsScheduleStore = create<TeamsScheduleState>((set, get) => {
  const getAccessToken = async (): Promise<string | null> => {
    if (!msalInstance) {
      msalInstance = await initializeMsal();
    }

    try {
      const account = msalInstance.getAllAccounts()[0];
      if (!account) return null;

      const response = await msalInstance.acquireTokenSilent({
        scopes: ['Calendars.Read', 'User.Read'],
        account
      });

      return response.accessToken;
    } catch {
      return null;
    }
  };

  const fetchEventList = async (accessToken: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/calendar/events?$filter=start/dateTime ge '${today.toISOString()}' and end/dateTime le '${tomorrow.toISOString()}'&$orderby=start/dateTime`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': `outlook.timezone="${Intl.DateTimeFormat().resolvedOptions().timeZone}"`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Teams events: ${response.statusText}`);
    }

    return response.json();
  };

  const processEventList = (data: { value: TeamsCalendarItem[] }): TeamsEvent[] => {
    if (!data.value) return [];

    return data.value
      .map((item) => {
        const startTime = new Date(item.start.dateTime);
        const endTime = new Date(item.end.dateTime);

        return {
          id: item.id,
          title: item.subject || 'Untitled Event',
          time: `${formatTime(startTime)} - ${formatTime(endTime)}`,
          joinUrl: item.onlineMeeting?.joinUrl,
          startTime,
          endTime
        };
      })
      .sort((a, b) => {
        // Sort by start time (earliest first)
        return a.startTime.getTime() - b.startTime.getTime();
      });
  };

  // Add a helper function for consistent time formatting
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return {
    events: [],
    loading: false,
    teamsAuthenticated: false,
    msalInstance: null,

    checkAuthState: async () => {
      try {
        if (!msalInstance) {
          msalInstance = await initializeMsal();
        }
        await msalInstance.handleRedirectPromise();
        const accounts = msalInstance.getAllAccounts();
        const isAuthenticated = accounts.length > 0;

        // If we have an authenticated account, set it as active
        if (isAuthenticated && accounts[0]) {
          msalInstance.setActiveAccount(accounts[0]);
        }

        set({ teamsAuthenticated: isAuthenticated, msalInstance });

        // If authenticated, refresh the events
        if (isAuthenticated) {
          get().fetchEvents();
        }
      } catch (error) {
        handleException(error);
        set({ teamsAuthenticated: false });
      }
    },

    authenticate: async () => {
      try {
        if (!msalInstance) {
          msalInstance = await initializeMsal();
        }

        const loginRequest = {
          scopes: ['Calendars.Read', 'User.Read'],
          prompt: 'select_account',
          redirectStartPage: window.location.href
        };

        // Try silent token acquisition first
        try {
          const account = msalInstance.getActiveAccount();
          if (account) {
            const response = await msalInstance.acquireTokenSilent({
              ...loginRequest,
              account
            });
            if (response) {
              set({ teamsAuthenticated: true });
              get().fetchEvents();
              return;
            }
          }
        } catch {
          // Silent token acquisition failed, fall back to popup
          handleException('Silent token acquisition failed, using popup');
        }

        // If silent token acquisition fails, use popup
        const response = await msalInstance.loginPopup(loginRequest);
        if (response.account) {
          msalInstance.setActiveAccount(response.account);
          set({ teamsAuthenticated: true });
          get().fetchEvents();
        }
      } catch (error) {
        handleException(error);
      }
    },

    disconnect: async () => {
      try {
        const accounts = msalInstance?.getAllAccounts();
        if (!accounts) return;
        for (const account of accounts) {
          await msalInstance?.logoutPopup({ account });
        }
        set({ teamsAuthenticated: false, events: [] });
      } catch (error) {
        handleException(error);
      }
    },

    fetchEvents: async () => {
      set({ loading: true });
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          set({ teamsAuthenticated: false });
          throw new Error('No access token available');
        }

        const data = await fetchEventList(accessToken);
        const events = processEventList(data);
        set({ events });
      } catch (error) {
        handleException(error);
        set({ events: [] });
      } finally {
        set({ loading: false });
      }
    },
  };
});

// Initialize MSAL and check auth state when the module loads
initializeMsal()
  .then(async () => {
    const store = useTeamsScheduleStore.getState();
    await store.checkAuthState();
  })
  .catch(handleException);
